import React, { useCallback, useEffect, useRef, useState } from 'react';
import ResetFiltersButton from '../../components/ResetFiltersButton';
import { reportsAPI } from '../../services/authService';
import { formatDateTimeIN } from '../../utils/dateFormat';
import { openPrintWindow } from '../../utils/exportUtils';
import { SENIOR_MANAGEMENT_PRINT_STYLES } from '../../config/printConfig';
import SeniorManagementReportContent from './seniorManagement/SeniorManagementReportContent';
import { buildCsv, downloadText } from './seniorManagement/reportUtils';
import './seniorManagement/seniorManagementReport.css';

const defaultDateTo = () => {
  const d = new Date();
  return d.toISOString().slice(0, 10);
};

const defaultDateFrom = () => {
  const d = new Date();
  d.setDate(d.getDate() - 30);
  return d.toISOString().slice(0, 10);
};

const SeniorManagementReport = ({
  projectFilter = 'all',
}) => {
  const [dateFrom, setDateFrom] = useState(defaultDateFrom);
  const [dateTo, setDateTo] = useState(defaultDateTo);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const printRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        from: dateFrom,
        to: dateTo,
        projectId: projectFilter === 'all' ? 'all' : String(projectFilter),
      };
      const res = await reportsAPI.getSeniorManagement(params);
      setData(res.data);
    } catch (e) {
      setData(null);
      const status = e.response?.status;
      if (status === 404) {
        setError(
          'Senior Management report API was not found (404). Deploy the latest backend and restart the API process so GET /api/reports/senior-management is available.'
        );
      } else {
        setError(e.response?.data?.message || e.message || 'Unable to load report.');
      }
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, projectFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handlePrint = () => {
    if (!data || !printRef.current) return;
    const m = data.meta || {};
    const subtitle = [
      `Reporting period: ${m.from} to ${m.to}`,
      m.projectName ? `Project: ${m.projectName}` : 'Portfolio: all projects in scope',
      `Generated: ${formatDateTimeIN(new Date(m.generatedAt))}`,
      `Generated by: ${m.generatedByName || ''} (${m.generatedByEmail || ''})`,
    ].join('\n');
    openPrintWindow({
      title: 'Senior Management Information Report',
      subtitle,
      bodyHtml: printRef.current.innerHTML,
      extraStyles: SENIOR_MANAGEMENT_PRINT_STYLES,
    });
  };

  const exportSummaryCsv = () => {
    if (!data?.summary) return;
    const rows = Object.entries(data.summary).map(([key, val]) => ({ key, value: val }));
    downloadText(
      `senior-management-summary-${data.meta?.from || 'from'}-${data.meta?.to || 'to'}.csv`,
      buildCsv(rows, [
        { key: 'key', label: 'Field' },
        { key: 'value', label: 'Value' },
      ]),
    );
  };

  const navLinks = [
    { href: '#sm-cover', label: 'Cover' },
    { href: '#sm-exec', label: 'Executive summary' },
    { href: '#sm-portfolio', label: 'Portfolio' },
    { href: '#sm-corr', label: 'Correspondence' },
    { href: '#sm-ra', label: 'RA bills' },
    { href: '#sm-bg', label: 'BG / Insurance' },
    { href: '#sm-ms', label: 'Milestones' },
    { href: '#sm-dwg', label: 'Drawings' },
    { href: '#sm-dev', label: 'Deviations' },
    { href: '#sm-qaqc', label: 'QAQC' },
    { href: '#sm-clr', label: 'Clearance' },
    { href: '#sm-scope', label: 'Agreement' },
    { href: '#sm-comp', label: 'Comparison' },
    { href: '#sm-annex', label: 'Annexures' },
  ];

  const meta = data?.meta;

  return (
    <div className="sm-report-root">
      <div className="filter-bar" style={{ flexWrap: 'wrap', gap: 10, alignItems: 'flex-end' }}>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 12, color: '#64748b' }}>From</label>
          <input type="date" className="form-input" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label style={{ fontSize: 12, color: '#64748b' }}>To</label>
          <input type="date" className="form-input" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
        <button type="button" className="btn-primary" onClick={load} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </button>
        <ResetFiltersButton
          onClick={() => {
            setDateFrom(defaultDateFrom());
            setDateTo(defaultDateTo());
          }}
          disabled={loading}
        />
        <button type="button" className="btn-secondary" onClick={handlePrint} disabled={!data}>
          Print / PDF
        </button>
        <button type="button" className="btn-secondary" onClick={exportSummaryCsv} disabled={!data}>
          Export summary CSV
        </button>
      </div>

      <p className="sm-toolbar-note" style={{ marginTop: 12 }}>
        Management information only: counts, values, dates, statuses, and references. Reporting period filters correspondence entry dates; chain and master counts reflect records within the selected project scope.
      </p>

      <nav className="sm-nav-pills" aria-label="Report sections">
        {navLinks.map((l) => (
          <a key={l.href} href={l.href}>
            {l.label}
          </a>
        ))}
      </nav>

      {error && <p style={{ color: '#b91c1c', marginTop: 12 }}>{error}</p>}

      {loading && !data && <p style={{ marginTop: 16, color: '#64748b' }}>Loading report…</p>}

      <div ref={printRef} className="sm-report-print">
        <SeniorManagementReportContent data={data} meta={meta} />
      </div>
    </div>
  );
};

export default SeniorManagementReport;
